import { useCallback, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { MdCheckCircle, MdClose, MdCloudUpload, MdError, MdInsertDriveFile } from 'react-icons/md'
import {
    confirmUpload,
    createUploadIntent,
    uploadMultipart,
    uploadToSignedUrl,
} from '@/lib/api/storage/storage-api'
import { formatBytes, readImageDimensions, readMediaDuration } from './storage-utils'

interface UploadItem {
    file: File
    progress: number
    status: 'pending' | 'uploading' | 'done' | 'error'
    error?: string
}

const toItems = (files: File[]): UploadItem[] =>
    files.map(file => ({ file, progress: 0, status: 'pending' }))

interface Props {
    isOpen: boolean
    folderId: number | null
    onClose: () => void
    onCompleted: () => void
    /**
     * Arquivos já escolhidos — vêm do arraste sobre a página.
     *
     * Só são lidos na MONTAGEM. Quem passa a prop precisa trocar a `key` do
     * componente a cada soltura, para que uma nova leva não seja ignorada por
     * cair no mesmo componente montado.
     */
    initialFiles?: File[]
    /** Dispara o envio sozinho, sem esperar o clique em "Enviar". */
    autoStart?: boolean
}

/**
 * Envio de arquivos.
 *
 * O binário vai direto para o R2 pela URL pré-assinada — o backend só emite a
 * autorização e confirma o resultado. Por isso o progresso vem do XHR do
 * próprio navegador, e não de uma requisição à API.
 */
export default function StorageUploadModal({
    isOpen,
    folderId,
    onClose,
    onCompleted,
    initialFiles,
    autoStart = false,
}: Props) {
    const [items, setItems] = useState<UploadItem[]>(() => toItems(initialFiles ?? []))
    const [dragging, setDragging] = useState(false)
    const [sending, setSending] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const autoStartedRef = useRef(false)

    const addFiles = useCallback((files: FileList | File[]) => {
        const list = Array.from(files).map<UploadItem>(file => ({
            file,
            progress: 0,
            status: 'pending',
        }))
        setItems(previous => [...previous, ...list])
    }, [])

    const updateItem = (index: number, patch: Partial<UploadItem>) => {
        setItems(previous =>
            previous.map((item, i) => (i === index ? { ...item, ...patch } : item)),
        )
    }

    const sendOne = async (item: UploadItem, index: number): Promise<boolean> => {
        updateItem(index, { status: 'uploading', progress: 0 })

        const dimensions = await readImageDimensions(item.file)
        const durationMs = await readMediaDuration(item.file)

        const intent = await createUploadIntent({
            name: item.file.name,
            mimeType: item.file.type || 'application/octet-stream',
            sizeBytes: item.file.size,
            folderId,
            ...(dimensions ? { width: dimensions.width, height: dimensions.height } : {}),
            ...(durationMs ? { durationMs } : {}),
        })

        if (!intent.success || !intent.result) {
            updateItem(index, {
                status: 'error',
                error: intent.error || 'Não foi possível iniciar o envio',
            })
            return false
        }

        const { fileId, strategy, uploadUrl, partUrls, partSize, uploadId } = intent.result

        try {
            if (strategy === 'multipart' && partUrls && partSize) {
                const parts = await uploadMultipart(partUrls, item.file, partSize, percent =>
                    updateItem(index, { progress: percent }),
                )
                const confirmed = await confirmUpload(fileId, { uploadId, parts })
                if (!confirmed.success) throw new Error(confirmed.error)
            } else if (uploadUrl) {
                await uploadToSignedUrl(
                    uploadUrl,
                    item.file,
                    item.file.type || 'application/octet-stream',
                    percent => updateItem(index, { progress: percent }),
                )
                const confirmed = await confirmUpload(fileId)
                if (!confirmed.success) throw new Error(confirmed.error)
            } else {
                throw new Error('Resposta de envio incompleta')
            }

            updateItem(index, { status: 'done', progress: 100 })
            return true
        } catch (error) {
            updateItem(index, {
                status: 'error',
                error: (error as Error).message || 'Falha no envio',
            })
            return false
        }
    }

    const handleSend = async () => {
        const pending = items
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => item.status === 'pending' || item.status === 'error')

        if (!pending.length) return

        setSending(true)
        let succeeded = 0

        // Sequencial de propósito: enviar 20 arquivos grandes em paralelo
        // satura o link do usuário e faz todos ficarem lentos ao mesmo tempo.
        for (const { item, index } of pending) {
            const ok = await sendOne(item, index)
            if (ok) succeeded++
        }

        setSending(false)

        if (succeeded) {
            toast.success(
                succeeded === 1
                    ? 'Arquivo enviado com sucesso'
                    : `${succeeded} arquivos enviados com sucesso`,
            )
            onCompleted()
        }
        if (succeeded < pending.length) {
            toast.error('Alguns arquivos não puderam ser enviados')
        }
    }

    // Arquivo solto sobre a página já chega decidido: o usuário mirou a pasta e
    // soltou. Pedir mais um clique em "Enviar" seria burocracia.
    useEffect(() => {
        if (!isOpen || !autoStart || autoStartedRef.current) return
        if (!items.length) return

        autoStartedRef.current = true
        void handleSend()
        // `handleSend` é recriada a cada render; a trava é o `autoStartedRef`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, autoStart, items.length])

    const handleClose = () => {
        if (sending) return
        setItems([])
        onClose()
    }

    if (!isOpen) return null

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-2xl">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Enviar arquivos</h3>
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost btn-circle"
                        onClick={handleClose}
                        disabled={sending}
                    >
                        <MdClose className="text-xl" />
                    </button>
                </div>

                <div
                    className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer ${
                        dragging
                            ? 'border-primary bg-primary/5'
                            : 'border-base-300 hover:border-primary/50'
                    }`}
                    onDragOver={event => {
                        event.preventDefault()
                        setDragging(true)
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={event => {
                        event.preventDefault()
                        setDragging(false)
                        if (event.dataTransfer.files?.length) addFiles(event.dataTransfer.files)
                    }}
                    onClick={() => inputRef.current?.click()}
                >
                    <MdCloudUpload className="text-5xl mx-auto text-base-content/40 mb-2" />
                    <p className="font-medium">
                        Arraste os arquivos aqui ou clique para selecionar
                    </p>
                    <p className="text-sm text-base-content/60 mt-1">
                        Vários arquivos de uma vez são aceitos
                    </p>
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={event => {
                            if (event.target.files?.length) addFiles(event.target.files)
                            event.target.value = ''
                        }}
                    />
                </div>

                {items.length > 0 && (
                    <div className="mt-4 max-h-72 overflow-y-auto space-y-2">
                        {items.map((item, index) => (
                            <div
                                key={`${item.file.name}-${index}`}
                                className="flex items-center gap-3 p-3 bg-base-200 rounded-lg"
                            >
                                <MdInsertDriveFile className="text-2xl text-base-content/50 shrink-0" />

                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">
                                        {item.file.name}
                                    </p>
                                    <p className="text-xs text-base-content/60">
                                        {formatBytes(item.file.size)}
                                    </p>
                                    {item.status === 'uploading' && (
                                        <progress
                                            className="progress progress-primary w-full h-1.5 mt-1"
                                            value={item.progress}
                                            max={100}
                                        />
                                    )}
                                    {item.status === 'error' && (
                                        <p className="text-xs text-error mt-1">{item.error}</p>
                                    )}
                                </div>

                                {item.status === 'done' && (
                                    <MdCheckCircle className="text-xl text-success shrink-0" />
                                )}
                                {item.status === 'error' && (
                                    <MdError className="text-xl text-error shrink-0" />
                                )}
                                {item.status === 'pending' && !sending && (
                                    <button
                                        type="button"
                                        className="btn btn-xs btn-ghost btn-circle"
                                        onClick={() =>
                                            setItems(previous =>
                                                previous.filter((_, i) => i !== index),
                                            )
                                        }
                                    >
                                        <MdClose />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <div className="modal-action">
                    <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={handleClose}
                        disabled={sending}
                    >
                        Fechar
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSend}
                        disabled={sending || !items.some(i => i.status !== 'done')}
                    >
                        {sending && <span className="loading loading-spinner loading-sm" />}
                        Enviar
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={handleClose} />
        </div>
    )
}
